import React, { useCallback } from "react";

import { faTableLayout } from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useModules } from "src/api/modulesApi";
import { useWorkspaceFeatureGates } from "src/api/queries";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { DEFAULT_STRING_LENGTH } from "src/base-components/schemaKit/validators";
import { Textarea } from "src/base-components/Textarea";
import { IconPicker } from "src/cases/common/IconPicker";
import {
  useCaseTemplateVersionFromWorkspace,
  useCreateTemplateFromExisting,
} from "src/cases/queries/templates";
import {
  isEntityRefProperty,
  isEventRefProperty,
  type TransformContext,
  transformTemplateVersion,
} from "src/cases/templates/templateTransformations";
import { AVAILABLE_ICONS } from "src/cases/templates/utils";
import { WorkspaceTemplateSelector } from "src/cases/templates/WorkspaceTemplateSelector";
import type { CaseTemplateVersionAttributes } from "src/clients/case-management";
import { ModuleKind, type WorkspaceDataplane } from "src/clients/flow-api";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { OrgAndWorkspaceDropdown } from "src/flowsOverview/OrgAndWorkspaceDropdown";
import { getUrlToCaseTemplateVersionPage } from "src/router/urls";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

type CreateFromExistingForm = {
  orgWorkspace: {
    orgId: string;
    workspaceId: string;
  };
  template: {
    templateId: string;
    versionId: string;
  };
  templateName: string;
  templateDescription: string;
  templateIcon: string;
};

type CreateFromExistingTemplateModalProps = {
  open: boolean;
  onClose: () => void;
  onCreateSuccess?: () => void;
};

const hasExternalReferences = (
  attributes: CaseTemplateVersionAttributes,
): boolean => {
  const hasFlowReferences =
    !!attributes.case_creation_flow_details?.flow_slug ||
    Object.values(attributes.case_actions ?? {}).some(
      (action) => !!action.flow?.flow_id,
    );

  const hasPropertyReferences = Object.values(
    attributes.case_properties ?? {},
  ).some((prop) => isEntityRefProperty(prop) || isEventRefProperty(prop));

  const hasQueueReferences = Object.values(
    attributes.workflow_rules ?? {},
  ).some((rule) =>
    rule.actions?.some((action) =>
      action.automation?.some(
        (auto) => auto.field === "queue_id" && auto.value,
      ),
    ),
  );

  return hasFlowReferences || hasPropertyReferences || hasQueueReferences;
};

const useCaseManagementFilter = () => {
  const { data: modules } = useModules({ kind: ModuleKind.CASE_MANAGEMENT });

  return useCallback(
    (workspace: WorkspaceDataplane) =>
      modules?.some(
        (m) =>
          m.kind === ModuleKind.CASE_MANAGEMENT &&
          m.workspaceId === workspace.id &&
          m.spec !== null,
      ) ?? false,
    [modules],
  );
};

export const CreateFromExistingTemplateModal: React.FC<
  CreateFromExistingTemplateModalProps
> = ({ open, onClose, onCreateSuccess }) => {
  const navigate = useNavigate();
  const { orgs, workspace: currentWorkspace } = useWorkspaceAndOrg();
  const filterWorkspaces = useCaseManagementFilter();
  const targetFeatureGates = useWorkspaceFeatureGates(currentWorkspace?.id);
  const createTemplateMutation = useCreateTemplateFromExisting();

  const form = useForm<CreateFromExistingForm>({
    mode: "onChange",
    defaultValues: {
      orgWorkspace: {
        orgId: "",
        workspaceId: "",
      },
      template: {
        templateId: "",
        versionId: "",
      },
      templateName: "",
      templateDescription: "",
      templateIcon: "shield-halved",
    },
  });

  const isPending =
    form.formState.isSubmitting || createTemplateMutation.isPending;

  const selectedWorkspaceId = form.watch("orgWorkspace.workspaceId");
  const selectedTemplate = form.watch("template");
  const selectedWorkspace = orgs
    .flatMap((org) => org.workspaces)
    .find((ws) => ws.id === selectedWorkspaceId);

  const queryEnabled =
    !!selectedWorkspace?.base_url &&
    !!selectedTemplate.templateId &&
    !!selectedTemplate.versionId;

  const { data: sourceVersionData } = useCaseTemplateVersionFromWorkspace(
    selectedWorkspace?.base_url,
    selectedTemplate.templateId,
    selectedTemplate.versionId,
    {
      enabled: queryEnabled,
    },
  );

  const onSubmit = async (data: CreateFromExistingForm) => {
    if (!sourceVersionData || !currentWorkspace) return;

    const isCrossWorkspace = selectedWorkspaceId !== currentWorkspace.id;

    const context: TransformContext = {
      sourceWorkspaceId: selectedWorkspaceId,
      targetWorkspaceId: currentWorkspace.id,
      targetFeatureGates,
    };

    const transformed = transformTemplateVersion(
      sourceVersionData.attributes,
      context,
    );

    try {
      const { template, version } = await createTemplateMutation.mutateAsync({
        templateName: data.templateName,
        templateDescription: data.templateDescription,
        templateIcon: data.templateIcon,
        transformedVersionData: transformed,
      });

      const versionName = version.attributes.version_name ?? "v1.0";
      const templateUrl = getUrlToCaseTemplateVersionPage(
        currentWorkspace.organization_id,
        currentWorkspace.id,
        template.id,
        version.id,
      );

      if (
        isCrossWorkspace &&
        hasExternalReferences(sourceVersionData.attributes)
      ) {
        toastActions.warning({
          title: "Template created with missing configurations",
          description:
            "Some properties or queues may be missing in this workspace and require manual reconfiguration.",
          actionText: "View",
          onActionClick: () => navigate(templateUrl),
        });
      } else {
        toastActions.success({
          title: "Template created successfully",
          description: `${data.templateName}, ${versionName} case template version was created successfully!`,
          actionText: "View",
          onActionClick: () => navigate(templateUrl),
        });
      }

      onCreateSuccess?.();
      onClose();
    } catch (error) {
      toastActions.failure({
        title: "Template creation failed",
        description:
          (isAxiosError(error) && error.response?.data?.detail?.msg) ||
          "Something went wrong and the template could not be created. Please try again.",
      });
    }
  };

  return (
    <Modal afterLeave={() => form.reset()} open={open} onClose={onClose}>
      <Modal.Header description="Choose the case template and version you want to duplicate">
        Create from existing template
      </Modal.Header>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="flex flex-col gap-6">
            <div>
              <Label required>Select workspace</Label>
              <Controller
                control={form.control}
                name="orgWorkspace"
                render={({ field }) => (
                  <OrgAndWorkspaceDropdown
                    filterWorkspaces={filterWorkspaces}
                    orgs={orgs}
                    selected={field.value}
                    onSelect={(value) => {
                      field.onChange(value);
                      form.resetField("template");
                    }}
                  />
                )}
                rules={{
                  validate: (value) => {
                    if (!value.orgId || !value.workspaceId) {
                      return "Please select an organization and workspace";
                    }
                    return true;
                  },
                }}
              />
            </div>

            <div>
              <Label required>Create from</Label>
              <Controller
                control={form.control}
                name="template"
                render={({ field, fieldState }) => (
                  <>
                    <WorkspaceTemplateSelector
                      error={!!fieldState.error}
                      value={field.value}
                      workspaceBaseUrl={selectedWorkspace?.base_url}
                      onChange={field.onChange}
                    />
                    {fieldState.error && (
                      <ErrorHint>{fieldState.error.message}</ErrorHint>
                    )}
                  </>
                )}
                rules={{
                  validate: (value) => {
                    if (!value.templateId || !value.versionId) {
                      return "Please select a template and version";
                    }
                    return true;
                  },
                }}
              />
            </div>

            <div className="flex items-start gap-2">
              <Controller
                control={form.control}
                defaultValue="shield-halved"
                name="templateIcon"
                render={({ field }) => (
                  <IconPicker
                    options={AVAILABLE_ICONS}
                    placeholder={faTableLayout}
                    size="md"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <div className="flex flex-1 flex-col">
                <Label required>Case template name</Label>
                <Input
                  errored={!!form.formState.errors.templateName}
                  placeholder="Enter case template name"
                  fullWidth
                  {...form.register("templateName", {
                    required: "Template name is required",
                    maxLength: {
                      value: DEFAULT_STRING_LENGTH,
                      message: `Template name must be less than ${DEFAULT_STRING_LENGTH} characters`,
                    },
                  })}
                />
                {form.formState.errors.templateName?.message && (
                  <ErrorHint>
                    {form.formState.errors.templateName.message}
                  </ErrorHint>
                )}
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                errored={!!form.formState.errors.templateDescription}
                placeholder="Enter template description"
                rows={3}
                fullWidth
                {...form.register("templateDescription", {
                  maxLength: {
                    value: 500,
                    message: "Description must be less than 500 characters",
                  },
                })}
              />
              {form.formState.errors.templateDescription?.message && (
                <ErrorHint>
                  {form.formState.errors.templateDescription.message}
                </ErrorHint>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="create-from-existing-submit"
              disabled={
                !form.formState.isValid || isPending || !sourceVersionData
              }
              htmlType="submit"
              loading={isPending}
              variant="primary"
            >
              Create
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
